import { useRef, useState } from "react";
import { Ellipsis, Lock, Pencil, Reply, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { ForwardRecipientKind } from "@lytra/frontend-common/__generated__/graphql";
import { Badge } from "@lytra/frontend-common/components/v2/ui/badge";
import { Button } from "@lytra/frontend-common/components/v2/ui/button";
import { Disclosure } from "@lytra/frontend-common/components/v2/ui/disclosure";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@lytra/frontend-common/components/v2/ui/dropdown-menu";
import { IconButton } from "@lytra/frontend-common/components/v2/ui/icon-button";
import { TooltipProvider } from "@lytra/frontend-common/components/v2/ui/tooltip";
import { cn, formatDateTime, formatDateTimeRelative } from "@/lib/utils";
import { ClampedText } from "./ClampedText";
import { ThreadCard } from "./ThreadCard";
import { ForwardReplyRow } from "./ForwardReplyRow";
import {
  ForwardEntry,
  ForwardHeaderRecipients,
  type ForwardQuoteSource,
} from "./ForwardEntry";
import { NoteBody, type MentionableAgent } from "./NoteBody";
import { DeleteNoteDialog } from "./DeleteNoteDialog";
import { NoteComposer } from "./NoteComposer";
import type { TicketMessage } from "./noteThreads";
import { noteComposerTriggerId } from "./noteDraft";
import { personName } from "./ticketHelpers";

/**
 * What the card reads of a message; every `TicketMessage` fits. The send fields are optional:
 * a note carries none of them, only a send root (`isForward`) is read through them.
 */
export type NoteMessage = Pick<
  TicketMessage,
  | "id"
  | "contentText"
  | "receivedAt"
  | "sentAt"
  | "createdAt"
  | "editedAt"
  | "deletedAt"
  | "parentMessageId"
  | "agent"
> &
  Partial<
    Pick<
      TicketMessage,
      | "isNote"
      | "isInternal"
      | "isForward"
      | "forwardedMessageId"
      | "forwardMailStatus"
      | "forwardRecipients"
      | "forwardRequestStates"
      | "deliveryProblems"
      // A colleague's mail reply (internal, not a note) renders as mail content.
      | "contentHtml"
      | "emailFullContentText"
      | "emailFullContentHtml"
      | "emailFromAddress"
      | "emailFromName"
      | "attachments"
    >
  >;

/** A colleague's mail reply under a send: an internal row that is not a Lytra note (D-12, #717). */
const isMailReply = (message: NoteMessage): boolean =>
  Boolean(message.isInternal) && !message.isNote;

/** A thread with more replies than this starts collapsed (UI-SPEC: collapsing a thread). */
const EXPANDED_REPLY_LIMIT = 3;

const messageTime = (message: NoteMessage): string =>
  message.receivedAt ?? message.sentAt ?? message.createdAt;

interface NoteThreadProps {
  root: NoteMessage;
  /** Replies of any depth, flat, in time order (`groupTimeline`). */
  replies: readonly NoteMessage[];
  viewerId?: string;
  agentsById: ReadonlyMap<string, MentionableAgent>;
  /** A deep-linked message: forces its thread open and shows its body whole. */
  highlightedMessageId?: string;
  /** The message that shows the transient deep-link ring right now (ticket highlight, ~2.5 s). */
  ringedMessageId?: string | null;
  /**
   * Shows "Antworten" at the thread's foot and the reply composer under the last reply (D-13),
   * and the viewer's own messages offer edit and delete (D-08). Off in dialogs and archive views.
   */
  enableNotes?: boolean;
  /** The ticket the reply is written in; needed with `enableNotes`. */
  conversationId?: string;
  /**
   * Names the message a send forwarded (sender, time) for the entry's quote line; null when it is
   * not in the loaded thread. Without it (dialogs, archive views) the entry has no quote line.
   */
  describeMessage?: (id: string) => ForwardQuoteSource | null;
  /** Scrolls the forwarded message to the centre and rings it ("Anzeigen"). */
  onShowMessage?: (id: string) => void;
  /** Downloads an attachment of a colleague's mail reply. */
  onDownloadAttachment?: (attachmentId: string) => void;
  /** Places the card in the thread column (its side and width cap, `threadSide`). */
  className?: string;
}

/**
 * An internal note, or a send to colleagues (#717, D-07, D-12), as one card: dashed border,
 * subtle surface and a lock badge, so it never reads as a message sent to the customer. A send root renders as the send entry instead of a note: who sent what to whom,
 * with no actions menu, since a send can be neither edited nor recalled. Replies (D-13) sit
 * flat inside the card in time order, in one collapsible block. Every message element
 * carries `data-message-id`, which is how the ticket's highlight effect finds it.
 */
export function NoteThread({
  root,
  replies,
  viewerId,
  agentsById,
  highlightedMessageId,
  ringedMessageId,
  enableNotes,
  conversationId,
  describeMessage,
  onShowMessage,
  onDownloadAttachment,
  className,
}: NoteThreadProps) {
  const { t } = useTranslation();
  const replyButtonRef = useRef<HTMLButtonElement>(null);
  // One reply composer per thread, at its foot (GitLab discussion model). Every reply is
  // stored pointing at the root note, whatever message the writer read last.
  const [replying, setReplying] = useState(false);
  // Screen-reader announcement of the last thing saved here (a reply, an edit).
  const [status, setStatus] = useState("");
  // The message the author is asked about; kept after closing so the dialog can fade out.
  const [deleteTarget, setDeleteTarget] = useState<{
    id: string;
    hasReplies: boolean;
  } | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const highlightedHere =
    highlightedMessageId !== undefined &&
    (root.id === highlightedMessageId ||
      replies.some((reply) => reply.id === highlightedMessageId));

  // Forced open once per deep-link target; a later manual toggle wins. State adjusted
  // during render (not in an effect) so the target exists in the first painted frame.
  const [open, setOpen] = useState(replies.length <= EXPANDED_REPLY_LIMIT);
  const [forcedFor, setForcedFor] = useState<string | null>(null);
  if (highlightedHere && forcedFor !== highlightedMessageId) {
    setForcedFor(highlightedMessageId);
    setOpen(true);
  }

  // A reply under a send that went to someone by mail stays in Lytra: say so, so nobody
  // expects the outside colleague to receive it (UI-SPEC 4, confirm item 8).
  const replyCaption =
    root.isForward &&
    root.forwardRecipients?.some(
      (recipient) => recipient.kind === ForwardRecipientKind.Mail,
    )
      ? t("tickets.forward.reply.caption")
      : undefined;

  const lastReply = replies.at(-1);
  const canReply = Boolean(enableNotes && conversationId && !root.deletedAt);
  // Edit and delete need the ticket (drafts, refetch); the server still decides who may.
  const canEdit = Boolean(enableNotes && conversationId);

  const askDelete = (messageId: string) => {
    setDeleteTarget({
      id: messageId,
      // Replies hang off the root, so only the root can have any (D-11).
      hasReplies: messageId === root.id && replies.length > 0,
    });
    setDeleteOpen(true);
  };

  const focusAddNote = () => {
    // The deleted card is gone with its menu button; "Notiz hinzufügen" takes the focus (D-11).
    if (!conversationId) return;
    requestAnimationFrame(() =>
      document.getElementById(noteComposerTriggerId(conversationId))?.focus(),
    );
  };

  const openReply = () => {
    // The thread is open while a reply is being written in it; only the user's toggle closes it.
    setOpen(true);
    setStatus("");
    setReplying(true);
  };

  const closeReply = () => {
    setReplying(false);
    // The composer is gone; the button it came from takes the focus back.
    requestAnimationFrame(() => replyButtonRef.current?.focus());
  };

  return (
    <div
      data-slot="note-thread"
      className={cn(
        "flex w-full max-w-[65ch] min-w-0 flex-col gap-3 overflow-hidden rounded-xl border border-dashed border-border bg-transparent p-4",
        className,
      )}
    >
      {root.isForward ? (
        <ForwardCard
          message={root}
          viewerId={viewerId}
          agentsById={agentsById}
          clamp={root.id !== highlightedMessageId}
          ringed={root.id === ringedMessageId}
          describeMessage={describeMessage}
          onShowMessage={onShowMessage}
        />
      ) : (
        <NoteCard
          message={root}
          isRoot
          viewerId={viewerId}
          agentsById={agentsById}
          clamp={root.id !== highlightedMessageId}
          ringed={root.id === ringedMessageId}
          editable={canEdit}
          conversationId={conversationId}
          onAnnounce={setStatus}
          onDelete={askDelete}
        />
      )}
      {replies.length > 0 && (
        <Disclosure
          size="sm"
          className="items-end"
          open={open}
          onOpenChange={setOpen}
          title={t("tickets.notes.replyCount", { count: replies.length })}
          meta={
            !open && lastReply ? (
              <span className="pl-2 text-caption text-ink-muted">
                {t("tickets.notes.lastReply", {
                  time: formatDateTimeRelative(messageTime(lastReply)),
                })}
              </span>
            ) : undefined
          }
          contentClassName="mt-3 ml-3 flex flex-col gap-3 border-l border-divider pl-4"
        >
          {replies.map((reply) =>
            isMailReply(reply) ? (
              <ForwardReplyRow
                key={reply.id}
                message={reply}
                onReply={canReply ? openReply : undefined}
                highlighted={reply.id === ringedMessageId}
                expanded={reply.id === highlightedMessageId}
                onDownloadAttachment={onDownloadAttachment}
              />
            ) : (
              <NoteCard
                key={reply.id}
                message={reply}
                isRoot={false}
                viewerId={viewerId}
                agentsById={agentsById}
                clamp={reply.id !== highlightedMessageId}
                ringed={reply.id === ringedMessageId}
                editable={canEdit}
                conversationId={conversationId}
                onAnnounce={setStatus}
                onDelete={askDelete}
              />
            ),
          )}
        </Disclosure>
      )}
      {canReply && !replying && (
        <div className="flex items-center gap-2">
          <Button
            ref={replyButtonRef}
            type="button"
            variant="ghost"
            size="sm"
            leadingIcon={Reply}
            onClick={openReply}
          >
            {t("tickets.notes.reply")}
          </Button>
        </div>
      )}
      {canReply && replying && conversationId && (
        <NoteComposer
          conversationId={conversationId}
          variant="reply"
          parentMessageId={root.id}
          viewerId={viewerId}
          caption={replyCaption}
          autoFocus
          onCancel={closeReply}
          onSaved={() => {
            setOpen(true);
            setStatus(t("tickets.notes.status.replySaved"));
            closeReply();
          }}
        />
      )}
      {deleteTarget && (
        <DeleteNoteDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          messageId={deleteTarget.id}
          hasReplies={deleteTarget.hasReplies}
          onDeleted={focusAddNote}
        />
      )}
      {status && (
        <span role="status" className="sr-only">
          {status}
        </span>
      )}
    </div>
  );
}

interface NoteCardProps {
  message: NoteMessage;
  isRoot: boolean;
  viewerId?: string;
  agentsById: ReadonlyMap<string, MentionableAgent>;
  clamp: boolean;
  /** Draws the deep-link ring around this message (not around the whole card). */
  ringed: boolean;
  /** The thread allows changing messages at all (`enableNotes` with a ticket). */
  editable: boolean;
  conversationId?: string;
  /** Asks the thread to announce `text` to screen readers. */
  onAnnounce: (text: string) => void;
  /** Opens the delete confirmation for this message (Löschen). Without it the menu has no such item. */
  onDelete?: (messageId: string) => void;
}

/** One message of the card: header, then the body (or the placeholder of a deleted note). */
function NoteCard({
  message,
  isRoot,
  viewerId,
  agentsById,
  clamp,
  ringed,
  editable,
  conversationId,
  onAnnounce,
  onDelete,
}: NoteCardProps) {
  const { t } = useTranslation();
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  const [editing, setEditing] = useState(false);
  const deleted = Boolean(message.deletedAt);
  // Only the author's own live messages carry the menu (D-08); the server enforces it (T-02-31).
  const isOwn =
    editable &&
    !deleted &&
    viewerId !== undefined &&
    message.agent?.id === viewerId;

  const closeEdit = () => {
    setEditing(false);
    // The editor is gone; the menu button it came from takes the focus back.
    requestAnimationFrame(() => menuTriggerRef.current?.focus());
  };
  const author = personName(message.agent ?? undefined);
  const time = deleted ? message.deletedAt : messageTime(message);

  return (
    <ThreadCard
      messageId={message.id}
      ringed={ringed}
      name={deleted ? null : (author ?? t("tickets.notes.unknownAuthor"))}
      personId={message.agent?.id}
      avatarSize={isRoot ? "sm" : "xs"}
      time={time}
      trailing={
        <>
          {!deleted && message.editedAt && (
            <span
              className="shrink-0 text-caption text-ink-muted"
              title={t("tickets.notes.editedTitle", {
                date: formatDateTime(message.editedAt),
              })}
            >
              {t("tickets.notes.edited")}
            </span>
          )}
          {isRoot && (
            <Badge tone="info" size="xs" icon={Lock} className="ml-auto">
              {t("tickets.notes.internalBadge")}
              <span className="sr-only">
                {t("tickets.notes.internalBadgeSr")}
              </span>
            </Badge>
          )}
          {isOwn && !editing && (
            <TooltipProvider>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <IconButton
                    ref={menuTriggerRef}
                    variant="ghost"
                    size="sm"
                    icon={Ellipsis}
                    label={t("tickets.notes.moreActions")}
                    className={cn("-my-1 shrink-0", !isRoot && "ml-auto")}
                  />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    icon={Pencil}
                    onSelect={() => setEditing(true)}
                  >
                    {t("tickets.notes.edit")}
                  </DropdownMenuItem>
                  {onDelete && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        icon={Trash2}
                        variant="destructive"
                        onSelect={() => onDelete(message.id)}
                      >
                        {t("tickets.notes.delete")}
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </TooltipProvider>
          )}
        </>
      }
    >
      {deleted ? (
        <p className="text-body text-ink-muted">{t("tickets.notes.deleted")}</p>
      ) : editing && conversationId ? (
        <NoteComposer
          conversationId={conversationId}
          variant="edit"
          messageId={message.id}
          parentMessageId={message.parentMessageId ?? undefined}
          initialText={message.contentText ?? ""}
          viewerId={viewerId}
          autoFocus
          onCancel={closeEdit}
          onSaved={() => {
            onAnnounce(t("tickets.notes.status.editSaved"));
            closeEdit();
          }}
        />
      ) : (
        <NoteBody
          text={message.contentText ?? ""}
          viewerId={viewerId}
          agentsById={agentsById}
          clamp={clamp}
        />
      )}
    </ThreadCard>
  );
}

interface ForwardCardProps {
  message: NoteMessage;
  viewerId?: string;
  agentsById: ReadonlyMap<string, MentionableAgent>;
  clamp: boolean;
  /** Draws the deep-link ring around this entry. */
  ringed: boolean;
  describeMessage?: (id: string) => ForwardQuoteSource | null;
  onShowMessage?: (id: string) => void;
}

/**
 * The send entry (UI-SPEC 4): header with the author, "hat an Kollegen gesendet", the time and
 * the "Intern" badge, then the agent's text (left out when empty) and the recipient list.
 * No menu, no edit, no delete: an entry that could be removed would hide that a mail left
 * the company (T-03-37). The text is React text, never HTML (T-03-36).
 */
function ForwardCard({
  message,
  viewerId,
  agentsById,
  clamp,
  ringed,
  describeMessage,
  onShowMessage,
}: ForwardCardProps) {
  const { t } = useTranslation();
  const author = personName(message.agent ?? undefined);
  const name = author ?? t("tickets.notes.unknownAuthor");
  const time = messageTime(message);
  const text = message.contentText?.trim() ? message.contentText : null;
  const source =
    message.forwardedMessageId && describeMessage
      ? describeMessage(message.forwardedMessageId)
      : null;
  const recipients = message.forwardRecipients ?? [];
  const toColleagues = recipients.some(
    (r) => r.kind === ForwardRecipientKind.Lytra,
  );

  return (
    <ThreadCard
      messageId={message.id}
      ringed={ringed}
      name={name}
      personId={message.agent?.id}
      avatarSize="sm"
      aside={t(
        toColleagues
          ? "tickets.forward.entry.headline"
          : "tickets.forward.entry.mailHeadline",
      )}
      afterAside={
        <ForwardHeaderRecipients
          recipients={recipients}
          viewerId={viewerId}
          agentsById={agentsById}
        />
      }
      time={time}
      trailing={
        <Badge tone="info" size="xs" icon={Lock} className="ml-auto shrink-0">
          {t("tickets.forward.entry.internal")}
          <span className="sr-only">
            {t("tickets.forward.entry.internalSr")}
          </span>
        </Badge>
      }
    >
      {text !== null && (
        <ClampedText textKey={text} clamp={clamp}>
          {text}
        </ClampedText>
      )}
      <ForwardEntry
        message={{
          id: message.id,
          forwardRecipients: message.forwardRecipients ?? [],
          forwardRequestStates: message.forwardRequestStates ?? [],
          forwardMailStatus: message.forwardMailStatus ?? null,
          deliveryProblems: message.deliveryProblems ?? [],
        }}
        viewerId={viewerId}
        agentsById={agentsById}
      />
      {source && message.forwardedMessageId && onShowMessage && (
        // The audit view: what went out, in the thread. Rarely needed, so a quiet button.
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="self-start"
          title={t("tickets.forward.entry.quote", {
            sender: source.sender,
            time: source.at,
          })}
          onClick={() => onShowMessage(message.forwardedMessageId!)}
        >
          {t("tickets.forward.entry.showSent")}
        </Button>
      )}
    </ThreadCard>
  );
}
